import AppStore from 'fetch/AppFetch';
import LoginFetch from './fetch/LoginFetch';
import ZywaveProxyFetch from './fetch/ZywaveProxyFetch';
import AppConfig from './appConfig';
import { ZY_LICENSE_KEY_AMS, USER_TYPE_ZYWAVE } from './lib/config/appConstants';
import { isZywaveUrl } from 'lib/AppHelpers';

// Constants for redirect loop detection
const REDIRECT_COUNTER_KEY = 'ZYWAVE-LEADS:REDIRECT_COUNTER';
const REDIRECT_URL_KEY = 'ZYWAVE-LEADS:REDIRECT_URL';
const MAX_REDIRECTS = 3;

/**
 * Class used to verify the user is logged in and can access data. Uses a mock login system for now
 *
 */
class AppAuth {
  static loggedIn = () => {
    return AppStore.fetchUserInfo() !== null;
  };

  /**
   * Logs into the service with the specified email and password, returns a Promise
   *
   * @param email: String
   * @param password: String
   * @returns {Promise.<TResult>}
   */
  static login = async (email, password) => {
    await LoginFetch.login(email, password);
    AppStore.storeToken('');
    return await AppAuth.getUserInfo();
  };

  /**
   * If the user has previously logged in, this will poll the server to validate their token is valid and retrieve
   * the API key needed to make further calls.
   *
   * @returns {Promise.<TResult>}
   */
  static getUserInfo = async () => {
    const json = await LoginFetch.findUserInfo();
    AppStore.storeUserInfo(json);
    return await Promise.resolve();
  };

  /**
   * Logs the user out
   */
  static logout = async () => {
    try {
      // Clear redirect tracking on logout (only if feature is enabled)
      if (AppConfig.enableRedirectZywaveDotCom) {
        AppAuth.clearRedirectTracking();
      }

      AppStore.deleteUserInfo();
      AppStore.deleteAgencyUser();
      AppStore.deleteZywaveUserFeatureAgencies();

      await LoginFetch.logout();
      AppStore.deleteToken();

      // Open login screen
      if (AppConfig.enableRedirectZywaveDotCom && isZywaveUrl()) {
        window.location.reload();
      } else {
        window.location = AppConfig.logoutUrl;
      }

      return await new Promise(() => {
        setTimeout(function () {});
      });
    } catch {
      // Silently handle logout errors
    }
  };

  /**
   * Fetch zywave api via zywave-proxy
   */
  static getZywaveFeatureLicenseAgencies = () => {
    return new Promise((resolve, reject) => {
      Promise.allSettled([ZywaveProxyFetch.getFeatureLicense(), ZywaveProxyFetch.searchAgencies()])
        .then((values) => {
          let info = {
            has_ams: false,
            agency_users: [],
          };

          const featureLicenseResponse = values[0]; // an object
          if (featureLicenseResponse && featureLicenseResponse.status === 'fulfilled') {
            info['has_ams'] =
              Object.keys(featureLicenseResponse.value).indexOf(ZY_LICENSE_KEY_AMS) !== -1
                ? true
                : false;
          }

          const agenciesResponse = values[1];
          if (agenciesResponse && agenciesResponse.status === 'fulfilled') {
            info['agency_users'] = agenciesResponse['value']['items'].map((user) => {
              return {
                firstName: user.firstName,
                lastName: user.lastName,
                isEnabled: user.isEnabled,
              };
            });
          }

          resolve(info);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  /**
   * Checks if user has access to broker search for EB
   * @param {Object} flags - LaunchDarkly flags object from FlagsContext
   * @returns {boolean} whether the user has access to broker search
   */
  static userHasBrokerSearchEb = (flags) => {
    const userInfo = AppStore.fetchUserInfo();

    // If the feature flag to enable broker search for all Zywave users is true
    if (flags?.ldMiedgeBrokerSearchForAll === true) {
      // Allow all Zywave users to access broker search
      return !!(userInfo && isZywaveUser(userInfo));
    }

    // Otherwise, check if user has specific broker search permission
    return !!(userInfo && userInfo['zywave_eb_broker_search'] === 1);
  };

  /**
   * @returns {boolean} whether the user has access to premium contacts or not
   */
  static userHasPremiumContacts = () => {
    const userInfo = AppStore.fetchUserInfo();

    return !!(userInfo && userInfo['zywave_is_premium_contacts'] === 1);
  };

  static userHasCommercialLinesExports = () => {
    const userInfo = AppStore.fetchUserInfo();

    return !!(userInfo && parseInt(userInfo['enable_pc_export_csv']) === 1);
  };

  static userHasEmployeeBenefitsExports = () => {
    const userInfo = AppStore.fetchUserInfo();

    return !!(userInfo && parseInt(userInfo['enable_eb_export_csv']) === 1);
  };

  /**
   * Check if redirect attempts have exceeded the maximum limit to detect infinite redirect loops
   * Only active when enableRedirectZywaveDotCom is true
   * @param {string} url - The URL being redirected to
   * @returns {boolean} - Returns true if max redirects exceeded, false if redirect should proceed
   */
  static hasExceededRedirectLimit = (url) => {
    // Only track redirects if the feature is enabled
    if (!AppConfig.enableRedirectZywaveDotCom) {
      return false;
    }

    const currentUrl = localStorage.getItem(REDIRECT_URL_KEY);
    const redirectCount = parseInt(localStorage.getItem(REDIRECT_COUNTER_KEY) || '0', 10);

    if (currentUrl === url) {
      // Same URL being redirected to again
      const newCount = redirectCount + 1;
      localStorage.setItem(REDIRECT_COUNTER_KEY, newCount.toString());

      if (newCount >= MAX_REDIRECTS) {
        // Max redirects exceeded, clear tracking and return true to indicate limit exceeded
        AppAuth.clearRedirectTracking();
        return true;
      }
    } else {
      // Different URL, reset counter
      localStorage.setItem(REDIRECT_URL_KEY, url);
      localStorage.setItem(REDIRECT_COUNTER_KEY, '1');
    }

    return false;
  };

  /**
   * Clear redirect tracking - call this when navigation succeeds without errors
   * Only active when enableRedirectZywaveDotCom is true
   */
  static clearRedirectTracking = () => {
    // Only clear tracking if the feature is enabled
    if (!AppConfig.enableRedirectZywaveDotCom) {
      return;
    }

    localStorage.removeItem(REDIRECT_COUNTER_KEY);
    localStorage.removeItem(REDIRECT_URL_KEY);
  };

  /**
   * Check if we're in an infinite redirect loop state
   * Only active when enableRedirectZywaveDotCom is true
   * @returns {boolean}
   */
  static isInRedirectLoop = () => {
    // Only check for redirect loops if the feature is enabled
    if (!AppConfig.enableRedirectZywaveDotCom) {
      return false;
    }

    const redirectCount = parseInt(localStorage.getItem(REDIRECT_COUNTER_KEY) || '0', 10);
    return redirectCount >= MAX_REDIRECTS;
  };
}

export const isZywaveUser = (userInfo) => {
  return (
    userInfo?.account_type === USER_TYPE_ZYWAVE ||
    window.location.href.toLowerCase().indexOf('zywave.com') > 0
  );
};

export default AppAuth;
